import { LEAD_COLLECTOR_STEPS, VIEW_TYPE, useCreateForm, Form } from '@entities/leadCollector';
import { UpdateFormPayload } from '@shared/api/lead-collector';
import { UnstyledButton } from '@shared/ui/unstyled-button';
import { Button, Switcher } from '@shared/ui/kit';
import InputClearable from '@shared/ui/input-clearable';
import { ArrowLeftIcon } from '@shared/assets/icons';
import { useToast } from '@shared/hooks/useToast';
import { useEffect, useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { styles } from './styles';

type FormState = {
  name: string;
  isWithAdults: boolean;
  maxExtraAdults: string;
  isWithChild: boolean;
  maxExtraChildren: string;
};

interface CreateFormProps {
  setStep?: (step: number) => void;
  prevStep?: number;
  setPrevStep?: (step: number) => void;
  setFormId?: (formId: string) => void;
  setViewType?: (viewType: number) => void;
  form?: Form;
  onSave?: (data?: Partial<UpdateFormPayload>) => Promise<void | string>;
  onCancel?: () => void;
}

export const CreateForm = ({
  setStep,
  prevStep,
  setPrevStep,
  setFormId,
  setViewType,
  form,
  onSave,
  onCancel,
}: CreateFormProps) => {
  const { showToast } = useToast();
  const { createForm, fetching } = useCreateForm();
  const [nameError, setNameError] = useState<string>('');

  const [formData, setFormData] = useState<FormState>({
    name: '',
    isWithAdults: false,
    maxExtraAdults: '',
    isWithChild: false,
    maxExtraChildren: '',
  });

  const handleCancelCreate = () => {
    if (onCancel) {
      onCancel();
      return;
    }

    setStep?.(prevStep || LEAD_COLLECTOR_STEPS.WELCOME);
    setPrevStep?.(LEAD_COLLECTOR_STEPS.WELCOME);
  };

  const handleChangeField = (field: keyof FormState, value: string | boolean) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleChangeName = (name: string) => {
    handleChangeField('name', name);
    setNameError('');
  };

  const handleCreateForm = async () => {
    const { name, isWithAdults, isWithChild } = formData;

    if (!name.trim()) {
      setNameError('Название шаблона не может быть пустым');
      return;
    }

    const maxExtraAdults = isWithAdults ? Number(formData.maxExtraAdults) : 0;
    const maxExtraChildren = isWithChild ? Number(formData.maxExtraChildren) : 0;

    const variables = { name, maxExtraAdults, maxExtraChildren };

    if (onSave) {
      const error = await onSave({
        data: { name: name, max_extra_children: maxExtraChildren, max_extra_adults: maxExtraAdults },
      });

      if (error) {
        setNameError(error);
      }

      return;
    }

    const { data, error } = await createForm(variables);

    if (error) {
      if (error?.message.includes('duplicate key')) {
        setNameError('Шаблон с таким названием уже существует ');
      } else {
        showToast('Ошибка при создании шаблона', 'error');
      }

      return;
    }

    setFormId?.(data.createForm.id);
    setViewType?.(VIEW_TYPE.CONSTRUCTOR);
    setStep?.(LEAD_COLLECTOR_STEPS.CONSTRUCTOR_FORM);
  };

  useEffect(() => {
    if (form) {
      setFormData({
        name: form.name,
        isWithAdults: !!form.maxExtraAdults,
        maxExtraAdults: `${form.maxExtraAdults || ''}`,
        isWithChild: !!form.maxExtraChildren,
        maxExtraChildren: `${form.maxExtraChildren || ''}`,
      });
    }
  }, []);

  return (
    <div {...stylex.props(styles.container)}>
      <UnstyledButton onClick={() => setStep?.(LEAD_COLLECTOR_STEPS.CHOOSE_FORM)}>
        <span {...stylex.props(styles.backButton)}>
          <ArrowLeftIcon /> К списку шаблонов
        </span>
      </UnstyledButton>

      <div {...stylex.props(styles.header)}>
        <p {...stylex.props(styles.title)}>Настройки шаблона</p>

        <div {...stylex.props(styles.actions)}>
          <Button variant="secondary" size="small" onClick={handleCancelCreate} isDisabled={fetching}>
            Отменить
          </Button>

          <Button size="small" onClick={handleCreateForm} isDisabled={fetching}>
            Сохранить и настроить форму
          </Button>
        </div>
      </div>

      <div {...stylex.props(styles.form)}>
        <div {...stylex.props(styles.fieldset)}>
          <div {...stylex.props(styles.fieldsetInfo)}>
            <span {...stylex.props(styles.label)}>Название шаблона</span>
            <span {...stylex.props(styles.description)}>
              Укажите название шаблона. Отображается в списке шаблонов мероприятия
            </span>
          </div>

          <InputClearable
            value={formData.name}
            changeFunction={handleChangeName}
            clearHandler={() => handleChangeField('name', '')}
            size="small"
            placeholder="Введите название"
            inputMessage={nameError}
            status={nameError ? 'error' : undefined}
          />
        </div>

        <div {...stylex.props(styles.fieldset)}>
          <div {...stylex.props(styles.fieldsetInfo)}>
            <span {...stylex.props(styles.label)}>Дополнительные взрослые гости</span>
            <span {...stylex.props(styles.description)}>
              Основной гость может оформить приглашение для дополнительных взрослых гостей
            </span>
          </div>

          <div {...stylex.props(styles.switcher)}>
            <Switcher
              changeFunction={() => handleChangeField('isWithAdults', !formData.isWithAdults)}
              isActive={formData.isWithAdults}
              variant="default"
              size="small"
            />

            <span {...stylex.props(styles.switcherLabel)}>Разрешить</span>
          </div>

          {formData.isWithAdults && (
            <InputClearable
              value={formData.maxExtraAdults}
              changeFunction={(value: string) => handleChangeField('maxExtraAdults', value)}
              clearHandler={() => handleChangeField('maxExtraAdults', '')}
              size="small"
              placeholder="Введите максимальное количество"
              type="number"
            />
          )}
        </div>

        <div {...stylex.props(styles.fieldset)}>
          <div {...stylex.props(styles.fieldsetInfo)}>
            <span {...stylex.props(styles.label)}>Маленькие гости</span>
            <span {...stylex.props(styles.description)}>Основной гость может оформить приглашение на ребенка</span>
          </div>

          <div {...stylex.props(styles.switcher)}>
            <Switcher
              changeFunction={() => handleChangeField('isWithChild', !formData.isWithChild)}
              isActive={formData.isWithChild}
              variant="default"
              size="small"
            />

            <span {...stylex.props(styles.switcherLabel)}>Разрешить</span>
          </div>

          {formData.isWithChild && (
            <InputClearable
              value={formData.maxExtraChildren}
              changeFunction={(value: string) => handleChangeField('maxExtraChildren', value)}
              clearHandler={() => handleChangeField('maxExtraChildren', '')}
              size="small"
              placeholder="Введите максимальное количество"
              type="number"
            />
          )}
        </div>
      </div>
    </div>
  );
};
